import { useEffect, useState, useRef } from "react";
import { CssBaseline, Box } from "@mui/material";
import NotificationAlert from "../shared/components/NotificationAlert";
import UsersDataGrid from './components/UsersDataGrid.jsx';
import UserViewDialog from './components/UserViewDialog.jsx';
import AppTheme from "../shared-theme/AppTheme";
import {
  chartsCustomizations,
  dataGridCustomizations,
  // datePickersCustomizations,
  treeViewCustomizations,
} from "../dashboard/theme/customizations";
import { sendPasswordResetEmail } from "firebase/auth";
import { useAuth } from '../shared/context/AuthContext';
import { resolveUidToEmail } from '../shared/utils/metadataUtils';
import SideMenu from "../shared/components/SideMenu";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useNavigate } from "react-router-dom";
import BreadCrumbsWithIcons from "../shared/components/BreadCrumbsWithIcons";
import * as adminService from '../services/adminService';
import { getHospital } from '../services/hospitalService';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  // ...datePickersCustomizations,
  ...treeViewCustomizations,
};


export default function Users(props) {
  const { auth, user, isAdmin, isAuthResolved } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState(([]));
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [filterModel, setFilterModel] = useState(/** @type {import('@mui/x-data-grid').GridFilterModel} */({ items: [] }));
  const pageTokensRef = useRef(/** @type {Record<number, string | null>} */ ({ 0: null }));
  const pendingPageResetRef = useRef(false);

  const [viewUser, setViewUser] = useState((null));
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);


  const handleRowUpdate = (user) => {
    setError(null);
    setLoading(true);
    const payload = {
      uid: user.uid || user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      hospitalId: user.hospitalId || '',
      isAdmin: user.isAdmin,
      isDemo: user.isDemo,
      isOffline: user.isOffline,
      mfaEnabled: user.mfaEnabled,
      photoURL: user.avatar || '',
      ...(user.password ? { password: user.password } : {}),
    };
    return adminService
      .updateUser(payload)
      .then(() => {
        setReloadKey((k) => k + 1);
      })
      .catch((err) => {
        console.error('updateUser failed', err);
        return Promise.reject(err);
      })
      .finally(() => setLoading(false));
  };

  const handleRowAdd = (user) => {
    setError(null);
    setLoading(true);
    const payload = {
      email: user.email,
      password: user.password,
      firstName: user.firstName,
      lastName: user.lastName,
      hospitalId: user.hospitalId || '',
      isAdmin: user.isAdmin,
      isDemo: user.isDemo,
      isOffline: user.isOffline,
      mfaEnabled: user.mfaEnabled,
      photoURL: user.avatar || ''
    };
    return adminService
      .createUser(payload)
      .then(() => {
        setPage(0);
        setReloadKey((k) => k + 1);
      })
      .catch((err) => {
        console.error('createUser failed', err);
        return Promise.reject(err);
      })
      .finally(() => setLoading(false));
  };

  const handleRowDelete = (user) => {
    setError(null);
    setLoading(true);
    const uid = user.uid || user.id;
    return adminService
      .deleteUser(uid)
      .then(() => {
        if (rows.length === 1 && page > 0) {
          setPage((p) => p - 1);
        }
        setReloadKey((k) => k + 1);
      })
      .catch((err) => {
        console.error('deleteUser failed', err);
        setError(err);
      })
      .finally(() => setLoading(false));
  };

  const handleRowReset = (user) => {
    if (!user || !user.email) return Promise.resolve();
    setError(null);
    setLoading(true);
    return sendPasswordResetEmail(auth, user.email)
      .then(() => {
        setSuccess(`Password reset email sent to ${user.email}`);
      })
      .catch((err) => {
        console.error('sendPasswordResetEmail failed', err);
        setError(err);
        return Promise.reject(err);
      })
      .finally(() => setLoading(false));
  };



  const handleRowView = (user) => {
    setError(null);
    const uid = user.uid || user.id;
    if (!uid) return;

    setViewLoading(true);
    adminService
      .getUser(uid)
      .then(async (full) => {
        let hospitalName = '';
        if (full?.hospitalId) {
          const hospital = /** @type {any} */ (await getHospital(full.hospitalId).catch(() => null));
          hospitalName = hospital?.name || '';
        }

        const createdBy = await resolveUidToEmail(adminService, full?.createdBy);
        const updatedBy = await resolveUidToEmail(adminService, full?.updatedBy);

        setViewUser({
          ...full,
          hospitalName,
          createdBy,
          updatedBy,
        });
        setViewOpen(true);
      })
      .catch((err) => {
        console.error('getUser for view failed', err);
        setError(err);
      })
      .finally(() => setViewLoading(false));
  };

  // Defense-in-depth: AdminRoute already gates this route on isAdmin, but
  // double-check here too in case this component is ever reached directly.
  useEffect(() => {
    if (!isAuthResolved) return;

    if (user) {
      setLoading(true);
      if (!isAdmin) {
        navigate('/dashboard', { replace: true });
      }
    } else {
      navigate('/login', { replace: true });
      setLoading(false);
    }

    setAuthChecked(true);
  }, [isAuthResolved, user, isAdmin, navigate]);

  const activeFilterItem = filterModel.items.find((item) => {
    const columnField = /** @type {any} */ (item).columnField || /** @type {any} */ (item).field;
    return (columnField === 'firstName' || columnField === 'lastName' || columnField === 'email') && item.value?.trim();
  }) || null;

  const activeFilterField = activeFilterItem?.field || '';
  const activeFilterValue = (activeFilterItem?.value || '').trim();
  const activeFilterOperator = activeFilterItem?.operator === 'equals' ? 'equals' : 'startsWith';
  const isFiltered = Boolean(activeFilterField && activeFilterValue);

  const [debouncedFilter, setDebouncedFilter] = useState({
    field: activeFilterField,
    operator: activeFilterOperator,
    value: activeFilterValue,
  });
  const debouncedIsFiltered = Boolean(debouncedFilter.field && debouncedFilter.value);

  useEffect(() => {
    const debounceId = setTimeout(() => {
      setDebouncedFilter({
        field: activeFilterField,
        operator: activeFilterOperator,
        value: activeFilterValue,
      });
    }, 300);

    return () => clearTimeout(debounceId);
  }, [activeFilterField, activeFilterOperator, activeFilterValue]);

  const handleFilterModelChange = (nextFilterModel) => {
    const supportedFilterItem = (nextFilterModel?.items || []).find((item) => {
      const columnField = /** @type {any} */ (item).columnField || /** @type {any} */ (item).field;
      return columnField === 'firstName' || columnField === 'lastName' || columnField === 'email';
    });

    const normalizedFilterItem = supportedFilterItem
      ? {
          ...supportedFilterItem,
          operator: supportedFilterItem.operator === 'equals' ? 'equals' : 'startsWith',
          value: supportedFilterItem.value || '',
        }
      : null;

    setFilterModel({
      ...nextFilterModel,
      items: normalizedFilterItem ? [normalizedFilterItem] : [],
    });
    setPage(0);
    setReloadKey((k) => k + 1);
  };

  useEffect(() => {
    pageTokensRef.current = { 0: null };

    if (page !== 0) {
      pendingPageResetRef.current = true;
      setPage(0);
      return;
    }

    pendingPageResetRef.current = false;
  }, [pageSize, debouncedFilter.field, debouncedFilter.operator, debouncedFilter.value, debouncedIsFiltered]);

  useEffect(() => {
    pageTokensRef.current = { 0: null };
  }, [reloadKey]);

  /**
   * @param {number} targetPage
   * @param {number} targetPageSize
   */
  const resolvePageToken = async (targetPage, targetPageSize) => {
    if (targetPage <= 0) return null;

    if (Object.prototype.hasOwnProperty.call(pageTokensRef.current, targetPage)) {
      return pageTokensRef.current[targetPage];
    }

    const knownPages = Object.keys(pageTokensRef.current)
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value >= 0)
      .sort((left, right) => left - right);

    let currentPage = 0;
    let currentToken = null;

    for (const knownPage of knownPages) {
      if (knownPage < targetPage) {
        currentPage = knownPage;
        currentToken = pageTokensRef.current[knownPage];
      }
    }

    while (currentPage < targetPage) {
      const response = await adminService.listUsers({
        pageSize: targetPageSize,
        pageToken: currentToken,
        filterField: debouncedFilter.field || undefined,
        filterOperator: debouncedFilter.operator,
        filterValue: debouncedFilter.value || undefined,
      });

      if (!response?.nextPageToken) {
        break;
      }

      pageTokensRef.current[currentPage + 1] = response.nextPageToken;
      currentToken = response.nextPageToken;
      currentPage += 1;
    }

    return pageTokensRef.current[targetPage] ?? null;
  };

  const fetchPage = async () => {
    if (!authChecked || !auth.currentUser) return;
    setError(null);
    setLoading(true);
    try {
      const data = debouncedIsFiltered
        ? await adminService.listUsers({
            page,
            pageSize,
            pageToken: (await resolvePageToken(page, pageSize)) || undefined,
            filterField: debouncedFilter.field,
            filterOperator: debouncedFilter.operator,
            filterValue: debouncedFilter.value,
          })
        : await adminService.listUsers({ page, pageSize, pageToken: (await resolvePageToken(page, pageSize)) || undefined });

      const hits = (data && data.users) || [];
      if (!debouncedIsFiltered) {
        pageTokensRef.current[page + 1] = data?.nextPageToken ?? null;
      }

      setHasNextPage(Boolean(data?.nextPageToken));
      setTotalCount(typeof data?.totalCount === 'number' ? data.totalCount : -1);
      setRows(
        hits.map((user) => ({
          id: user.uid,
          uid: user.uid,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email,
          hospitalId: user.hospitalId || '',
          totalUploads: Number(user.totalUploads) || 0,
          lastUploadAt: user.lastUploadAt || null,
          isAdmin: user.isAdmin || false,
          isDemo: user.isDemo || false,
          isOffline: user.isOffline || false,
          mfaEnabled: user.mfaEnabled ?? false,
          totpEnrolled: user.totpEnrolled ?? false,
          emailVerified: user.emailVerified ?? false,
          avatar: user.photoURL || '',
        }))
      );
    } catch (err) {
      console.error('listUsers failed', err);
      setError(/** @type {any} */ (err));
      setRows([]);
      setTotalCount(-1);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pendingPageResetRef.current && page !== 0) {
      return;
    }

    pendingPageResetRef.current = false;
    fetchPage();
  }, [authChecked, page, pageSize, reloadKey, debouncedFilter.field, debouncedFilter.operator, debouncedFilter.value, debouncedIsFiltered]);

  if (!authChecked) return null;

  return (
    <AppTheme themeComponents={xThemeComponents} {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: "flex" }}>
        <SideMenu />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <BreadCrumbsWithIcons
            items={[
              { key: 'home', label: 'Home', to: '/dashboard', Icon: HomeOutlinedIcon },
              { key: 'users', label: 'Users', to: null, Icon: AccountCircleIcon },
            ]}
          />
          <NotificationAlert error={error} onClear={() => setError(null)} />
          <NotificationAlert
            severity="success"
            message={success}
            duration={5000}
            onClear={() => setSuccess('')}
          />
          <UsersDataGrid
            rows={rows}
            loading={loading || viewLoading}
            page={page}
            pageSize={pageSize}
            rowCount={totalCount}
            paginationMeta={!isFiltered ? { hasNextPage } : undefined}
            hasMore={hasNextPage}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(ps) => setPageSize(ps)}
            onRowView={handleRowView}
            onRowUpdate={handleRowUpdate}
            onRowAdd={handleRowAdd}
            onRowDelete={handleRowDelete}
            onRowReset={handleRowReset}
          />
          <UserViewDialog
            open={viewOpen}
            user={viewUser}
            onClose={() => setViewOpen(false)}
            onUserUpdated={() => setReloadKey((k) => k + 1)}
            onRefreshUser={(uid) => {
              adminService.getUser(uid).then((full) => {
                setViewUser((prev) => ({ ...prev, ...full }));
              }).catch(() => {});
            }}
          />
        </Box>
      </Box>
    </AppTheme>
  );
}
